// ChangePassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChangePassword.css';

function ChangePassword() {
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		currentPassword: '',
		newPassword: '',
		confirmPassword: ''
	});
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const [showPasswords, setShowPasswords] = useState({
		current: false,
		new: false,
		confirm: false
	});

	// 비밀번호 강도 체크
	const checkPasswordStrength = (password) => {
		if (password.length < 8) return { strength: 'weak', text: '약함', color: '#ef4444' };
		
		let strength = 0;
		if (password.length >= 10) strength++;
		if (/[a-z]/.test(password)) strength++;
		if (/[A-Z]/.test(password)) strength++;
		if (/[0-9]/.test(password)) strength++;
		if (/[^a-zA-Z0-9]/.test(password)) strength++;

		if (strength <= 2) return { strength: 'weak', text: '약함', color: '#ef4444' };
		if (strength <= 3) return { strength: 'medium', text: '보통', color: '#f59e0b' };
		return { strength: 'strong', text: '강함', color: '#10b981' };
	};

	const passwordStrength = formData.newPassword ? checkPasswordStrength(formData.newPassword) : null;

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		
		// 에러 초기화
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const togglePasswordVisibility = (field) => {
		setShowPasswords(prev => ({
			...prev,
			[field]: !prev[field]
		}));
	};

	const validate = () => {
		const newErrors = {};

		if (!formData.currentPassword) {
			newErrors.currentPassword = '현재 비밀번호를 입력해주세요.';
		}

		if (!formData.newPassword) {
			newErrors.newPassword = '새 비밀번호를 입력해주세요.';
		} else if (formData.newPassword.length < 8) {
			newErrors.newPassword = '비밀번호는 최소 8자 이상이어야 합니다.';
		} else if (formData.newPassword === formData.currentPassword) {
			newErrors.newPassword = '현재 비밀번호와 다른 비밀번호를 입력해주세요.';
		}

		if (!formData.confirmPassword) {
			newErrors.confirmPassword = '새 비밀번호를 다시 입력해주세요.';
		} else if (formData.newPassword !== formData.confirmPassword) {
			newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validate()) return;

		setLoading(true);

		try {
			const token = localStorage.getItem('access_token');
			
			await axios.post(
				'https://api.weartoday.kr/api/accounts/change-password/',
				{
					current_password: formData.currentPassword,
					new_password: formData.newPassword
				},
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);

			alert('비밀번호가 성공적으로 변경되었습니다.');
			navigate('/mypage');

		} catch (error) {
			console.error('비밀번호 변경 실패:', error);
			
			if (error.response?.status === 400) {
				if (error.response.data.current_password) {
					setErrors({ currentPassword: '현재 비밀번호가 올바르지 않습니다.' });
				} else {
					alert(error.response.data.detail || '비밀번호 변경에 실패했습니다.');
				}
			} else if (error.response?.status === 401) {
				alert('로그인이 만료되었습니다.');
				navigate('/login');
			} else {
				alert('비밀번호 변경 중 오류가 발생했습니다.');
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="change-password-page">
			<div className="change-password-container">
				<div className="password-header">
					<button onClick={() => navigate(-1)} className="back-btn">
						‹
					</button>
					<h1>비밀번호 변경</h1>
				</div>

				<form onSubmit={handleSubmit} className="password-form">
					{/* 현재 비밀번호 */}
					<div className="form-group">
						<label htmlFor="currentPassword">
							현재 비밀번호 <span className="required">*</span>
						</label>
						<div className="password-input-wrapper">
							<input
								type={showPasswords.current ? 'text' : 'password'}
								id="currentPassword"
								name="currentPassword"
								value={formData.currentPassword}
								onChange={handleChange}
								className={errors.currentPassword ? 'error' : ''}
								placeholder="현재 비밀번호를 입력하세요"
							/>
							<button
								type="button"
								className="toggle-password"
								onClick={() => togglePasswordVisibility('current')}
							>
								{showPasswords.current ? '🙈' : '👁️'}
							</button>
						</div>
						{errors.currentPassword && (
							<p className="error-message">{errors.currentPassword}</p>
						)}
					</div>

					{/* 새 비밀번호 */}
					<div className="form-group">
						<label htmlFor="newPassword">
							새 비밀번호 <span className="required">*</span>
						</label>
						<div className="password-input-wrapper">
							<input
								type={showPasswords.new ? 'text' : 'password'}
								id="newPassword"
								name="newPassword"
								value={formData.newPassword}
								onChange={handleChange}
								className={errors.newPassword ? 'error' : ''}
								placeholder="새 비밀번호를 입력하세요 (최소 8자)"
							/>
							<button
								type="button"
								className="toggle-password"
								onClick={() => togglePasswordVisibility('new')}
							>
								{showPasswords.new ? '🙈' : '👁️'}
							</button>
						</div>
						
						{/* 비밀번호 강도 표시 */}
						{formData.newPassword && passwordStrength && (
							<div className="password-strength">
								<div className="strength-bar">
									<div 
										className={`strength-fill ${passwordStrength.strength}`}
										style={{ 
											width: passwordStrength.strength === 'weak' ? '33%' : 
														 passwordStrength.strength === 'medium' ? '66%' : '100%',
											backgroundColor: passwordStrength.color
										}}
									/>
								</div>
								<p className="strength-text" style={{ color: passwordStrength.color }}>
									{passwordStrength.text}
								</p>
							</div>
						)}
						
						{errors.newPassword && (
							<p className="error-message">{errors.newPassword}</p>
						)}
						
						<p className="help-text">
							💡 영문, 숫자, 특수문자를 조합하여 8자 이상 입력하세요.
						</p>
					</div>

					{/* 새 비밀번호 확인 */}
					<div className="form-group">
						<label htmlFor="confirmPassword">
							새 비밀번호 확인 <span className="required">*</span>
						</label>
						<div className="password-input-wrapper">
							<input
								type={showPasswords.confirm ? 'text' : 'password'}
								id="confirmPassword"
								name="confirmPassword"
								value={formData.confirmPassword}
								onChange={handleChange}
								className={errors.confirmPassword ? 'error' : ''}
								placeholder="새 비밀번호를 다시 입력하세요"
							/>
							<button
								type="button"
								className="toggle-password"
								onClick={() => togglePasswordVisibility('confirm')}
							>
								{showPasswords.confirm ? '🙈' : '👁️'}
							</button>
						</div>
						
						{formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
							<p className="success-message">✅ 비밀번호가 일치합니다.</p>
						)}
						
						{errors.confirmPassword && (
							<p className="error-message">{errors.confirmPassword}</p>
						)}
					</div>

					{/* 버튼 */}
					<div className="form-actions">
						<button
							type="button"
							onClick={() => navigate(-1)}
							className="btn-cancel"
							disabled={loading}
						>
							취소
						</button>
						<button
							type="submit"
							className="btn-submit"
							disabled={loading}
						>
							{loading ? '변경 중...' : '비밀번호 변경'}
						</button>
					</div>
				</form>

				{/* 안내 사항 */}
				<div className="password-notice">
					<h3>⚠️ 비밀번호 변경 안내</h3>
					<ul>
						<li>비밀번호는 주기적으로 변경하는 것이 좋습니다.</li>
						<li>다른 사이트와 동일한 비밀번호 사용을 피해주세요.</li>
						<li>개인정보(이름, 생년월일 등)를 비밀번호로 사용하지 마세요.</li>
						<li>비밀번호 변경 후 모든 기기에서 재로그인이 필요합니다.</li>
					</ul>
				</div>
			</div>
		</div>
	);
}

export default ChangePassword;